import { Component, contentChildren, output, input, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgClass } from '@angular/common';

import { ApContentService } from '@ap/content';
import { ApFormErrorsComponent, ErrorMessages } from '@ap/form/errors';

import { ApButtonGroupItemComponent } from './item/button-group-item.component';
import content from './button-group.content.json';

/**
 * component for grouping a couple of buttons of which only one can be active at a time.
 **/
@Component({
    selector: 'ap-button-group',
    preserveWhitespaces: false,
    templateUrl: './button-group.component.html',
    styleUrls: ['./button-group.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ApButtonGroupComponent,
            multi: true
        }
    ],
    imports: [NgClass, FormsModule, ApFormErrorsComponent]
})
export class ApButtonGroupComponent implements ControlValueAccessor {
    private readonly contentService = inject(ApContentService);
    /**
     * The name of the set of buttons, will be used as name attribute for each button.
     */
    public readonly name = input<string>(undefined);

    /**
     * used to define a string that labels the current element. Use it in cases where a text label is not visible on the screen
     */
    public readonly ariaLabel = input<string>(undefined);

    /**
     * establishes relationships between objects and their label(s), and its value should be one or more element IDs, which refer to elements that have the text needed for labeling
     */
    public readonly ariaLabelledBy = input<string>(undefined);

    /**
     * Form control of the button group
     * @internal
     */
    public readonly formControl = input<FormControl<string>>(undefined);

    /**
     * Error message object or array of strings.
     * If array of strings: First string is always shown on error
     * If error message object: Key that matches current error is shown.
     */
    public readonly messages = input<ErrorMessages | string[]>(undefined);

    /**
     * Clicking on an item will emit the buttonGroupClicked event with the value.
     */
    public readonly buttonGroupClicked = output<string>();

    protected readonly _buttonItems = contentChildren(ApButtonGroupItemComponent);

    protected required: boolean;

    /**
     * Unique id of field that can be used to map aria fields
     */
    protected uniqueId = `button-group-${Math.round(Math.random() * 10000000)}`;

    /** @internal */
    public isDisabled: boolean;
    protected _value: string | number;
    get value(): any {
        return this._value;
    }

    set value(value: any) {
        if (!this.isDisabled) {
            this._value = value;
            this.onChange(this.value);
        }
    }
    /**
     * Json object that contains default content messages from form element
     */
    protected content: { [key: string]: any };

    constructor() {
        this.contentService.addContentItems(content);
        this.content = content;
    }

    /**
     * Set touched and dirty state if input blurs
     */
    protected onBlur(): void {
        this.onTouched();
    }

    /**
     * Function to call when the value changes.
     * @internal
     */
    public onChange = (value: any): void => {
        this._value = value;
    };

    /**
     * Function to call when the input is touched (when a button item is clicked).
     * @internal
     */
    public onTouched = (): void => {};

    /**
     * Allows Angular to initialize the model (selected button item).
     * @internal
     */
    public writeValue(value: any): void {
        this._value = value;
    }

    /**
     * Allows Angular to register a function to call when the model (selected button item) changes.
     * @internal
     */
    public registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    /**
     * Allows Angular to register a function to call when the input has been touched.
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Allows Angular to disable the input.
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    protected onButtonItemClicked(value: string): void {
        if (!this.isDisabled) {
            this.buttonGroupClicked.emit(value);
        }
    }
}
